/**
 * Aba "Médias" do funcionário — corte de médias de variáveis (27/08/2026,
 * pedido do dono): média cadastrada com VIGÊNCIA; a partir da competência de
 * corte ela entra no cálculo de férias/13º/rescisão (precedência: override do
 * processamento > vigente > derivada do histórico > zerada). Vigências
 * empilham (histórico preservado). Componentes em minutos ficam vivos
 * (Súmula 160 — monetizados com o valor-hora do pagamento); o campo "valores"
 * é o R$ médio das rubricas em valor (comissões, DSR reflexo, bônus…) e entra
 * direto, sem recomposição de DSR (modelo Domínio).
 */
import { useEffect, useState } from "react";
import { Alert, Badge, Button, Card, Group, Table, Text, TextInput, Title } from "@mantine/core";
import { api } from "../../api";
import { fmtCompetencia, fmtHhMm, fmtMoeda, parseHhMm, parseMoeda } from "../../format";
import { Carregando } from "../../componentes/Estados";
import { Confirmacao, notificarErroApi, notificarOk } from "../../componentes/notificacoes";

interface MediaVigente {
  id: string;
  vigenciaInicio: number;
  he50MinMedia: number | null;
  he100MinMedia: number | null;
  anMinMedia: number | null;
  valoresMediaCentavos: number | null;
  observacao: string | null;
  origem: string | null;
  criadoEm: string;
}

/** "1:53" ou "113,42" (minutos fracionários de média) → minutos. */
function parseMinutosMedia(texto: string): number {
  const t = texto.trim();
  if (t.includes(":")) return parseHhMm(t);
  const n = Number(t.replace(/\./g, "").replace(",", "."));
  if (!Number.isFinite(n) || n < 0) throw new Error(`Minutos inválidos: "${texto}" (use hh:mm ou minutos, ex.: 1:53 ou 113,42)`);
  return n;
}

/** "08/2026" → 202608. */
function parseVigencia(texto: string): number {
  const m = /^(\d{2})\/(\d{4})$/.exec(texto.trim());
  if (!m) throw new Error(`Vigência inválida: "${texto}" (use MM/AAAA, ex.: 08/2026)`);
  const mes = Number(m[1]);
  if (mes < 1 || mes > 12) throw new Error(`Mês inválido: "${texto}"`);
  return Number(m[2]) * 100 + mes;
}

function fmtMinMedia(min: number | null): string {
  if (min === null || min === undefined) return "—";
  const dec = min.toLocaleString("pt-BR", { maximumFractionDigits: 2 });
  return Number.isInteger(min) ? fmtHhMm(min) : `${dec} min (≈ ${fmtHhMm(Math.round(min))})`;
}

export function MediasTab({ funcionarioId }: { funcionarioId: string }) {
  const [medias, setMedias] = useState<MediaVigente[] | null>(null);
  const [vigencia, setVigencia] = useState("");
  const [he50, setHe50] = useState("");
  const [he100, setHe100] = useState("");
  const [an, setAn] = useState("");
  const [valores, setValores] = useState("");
  const [origem, setOrigem] = useState("");
  const [observacao, setObservacao] = useState("");
  const [excluindo, setExcluindo] = useState<MediaVigente | null>(null);
  const [salvando, setSalvando] = useState(false);

  const carregar = () =>
    api
      .get<{ medias: MediaVigente[] }>(`/funcionarios/${funcionarioId}/medias`)
      .then((r) => setMedias(r.medias))
      .catch((e) => {
        notificarErroApi(e);
        setMedias([]);
      });

  useEffect(() => {
    void carregar();
  }, [funcionarioId]);

  async function salvar() {
    try {
      setSalvando(true);
      const body = {
        vigenciaInicio: parseVigencia(vigencia),
        he50MinMedia: he50.trim() ? parseMinutosMedia(he50) : null,
        he100MinMedia: he100.trim() ? parseMinutosMedia(he100) : null,
        anMinMedia: an.trim() ? parseMinutosMedia(an) : null,
        valoresMediaCentavos: valores.trim() ? parseMoeda(valores) : null,
        origem: origem.trim() || null,
        observacao: observacao.trim() || null,
      };
      if (body.he50MinMedia === null && body.he100MinMedia === null && body.anMinMedia === null && body.valoresMediaCentavos === null) {
        throw new Error("Informe ao menos um componente (minutos de HE/AN e/ou valores em R$)");
      }
      await api.post(`/funcionarios/${funcionarioId}/medias`, body);
      notificarOk(`Média vigente a partir de ${fmtCompetencia(body.vigenciaInicio)} cadastrada`);
      setVigencia(""); setHe50(""); setHe100(""); setAn(""); setValores(""); setOrigem(""); setObservacao("");
      carregar();
    } catch (e) {
      notificarErroApi(e, "Revise os dados");
    } finally {
      setSalvando(false);
    }
  }

  async function excluir() {
    if (!excluindo) return;
    try {
      await api.del(`/funcionarios/${funcionarioId}/medias/${excluindo.id}`);
      notificarOk(`Média de ${fmtCompetencia(excluindo.vigenciaInicio)} excluída`);
      setExcluindo(null);
      carregar();
    } catch (e) {
      notificarErroApi(e);
    }
  }

  if (medias === null) return <Carregando />;

  return (
    <Card withBorder>
      <Title order={4} mb="xs">Médias de variáveis (férias, 13º, rescisão)</Title>
      <Alert color="blue" variant="light" mb="md">
        <Text size="sm">
          Cadastre aqui a média quando você souber o valor oficial (ex.: recibo da contabilidade). A partir da
          competência de corte, ela <b>entra no cálculo sozinha</b> — férias, 13º e rescisão passam a usá-la. Os
          minutos de hora extra ficam <b>vivos</b>: se o salário mudar, eles são recalculados com o valor-hora novo
          (Súmula 160). O campo de valores (R$) já vem pronto — some comissões, DSR e bônus do recibo. Um valor
          informado direto num recibo específico (override do processamento) continua tendo prioridade sobre este
          cadastro.
        </Text>
      </Alert>

      {medias.length === 0 ? (
        <Text c="dimmed" size="sm" mb="md">Nenhuma média cadastrada — o sistema deriva do histórico quando houver cobertura.</Text>
      ) : (
        <Table mb="md" data-testid="tabela-medias">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Vigente desde</Table.Th>
              <Table.Th>HE 50%/mês</Table.Th>
              <Table.Th>HE 100%/mês</Table.Th>
              <Table.Th>Adic. noturno/mês</Table.Th>
              <Table.Th>Valores/mês</Table.Th>
              <Table.Th>Origem</Table.Th>
              <Table.Th>Observação</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {medias.map((m, i) => (
              <Table.Tr key={m.id}>
                <Table.Td>
                  <Group gap="xs">
                    {fmtCompetencia(m.vigenciaInicio)}
                    {i === 0 && <Badge color="sillis" size="sm">atual</Badge>}
                  </Group>
                </Table.Td>
                <Table.Td>{fmtMinMedia(m.he50MinMedia)}</Table.Td>
                <Table.Td>{fmtMinMedia(m.he100MinMedia)}</Table.Td>
                <Table.Td>{fmtMinMedia(m.anMinMedia)}</Table.Td>
                <Table.Td>{fmtMoeda(m.valoresMediaCentavos)}</Table.Td>
                <Table.Td>{m.origem ?? "—"}</Table.Td>
                <Table.Td>{m.observacao ?? "—"}</Table.Td>
                <Table.Td>
                  <Button size="compact-xs" variant="subtle" color="red" onClick={() => setExcluindo(m)}>
                    Excluir
                  </Button>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      <Title order={5} mb="xs">Nova média (corte)</Title>
      <Group align="flex-end" gap="sm" wrap="wrap" mb="sm">
        <TextInput label="Vigente a partir de" placeholder="MM/AAAA" value={vigencia} onChange={(e) => setVigencia(e.currentTarget.value)} w={130} />
        <TextInput label="HE 50% (média/mês)" placeholder="hh:mm ou min" value={he50} onChange={(e) => setHe50(e.currentTarget.value)} w={150} />
        <TextInput label="HE 100% (média/mês)" placeholder="hh:mm ou min" value={he100} onChange={(e) => setHe100(e.currentTarget.value)} w={150} />
        <TextInput label="Adic. noturno (média/mês)" placeholder="hh:mm ou min" value={an} onChange={(e) => setAn(e.currentTarget.value)} w={170} />
        <TextInput label="Valores (R$/mês)" placeholder="0,00" value={valores} onChange={(e) => setValores(e.currentTarget.value)} w={140} />
        <TextInput label="Origem" placeholder="ex.: recibo Contmega 08/2026" value={origem} onChange={(e) => setOrigem(e.currentTarget.value)} w={220} />
        <TextInput label="Observação" value={observacao} onChange={(e) => setObservacao(e.currentTarget.value)} w={260} />
        <Button onClick={salvar} loading={salvando}>Cadastrar</Button>
      </Group>

      <Confirmacao
        aberta={excluindo !== null}
        titulo="Excluir média cadastrada"
        consequencia={
          excluindo
            ? `A média vigente desde ${fmtCompetencia(excluindo.vigenciaInicio)} deixa de valer. Os próximos cálculos voltam a derivar a média do histórico. Recibos já fechados não mudam.`
            : ""
        }
        rotuloConfirmar="Excluir"
        onConfirmar={excluir}
        onCancelar={() => setExcluindo(null)}
      />
    </Card>
  );
}
